'use client'

import { useLessonReviewProgress } from '@features/lesson-review/model'
import { useTranslation } from '@shared/config/i18n'
import { buildClassroomUrl } from '@shared/routes'
import { openConfirmDialogV2, TopNavigation } from '@shared/ui'
import { useRouter } from 'next/navigation'
import React from 'react'

import { Button, HStack, Typography, VStack } from '@podo-app/design-system-temp'

type Props = {
  title: React.ReactNode
  classId: number
}

export const LessonReviewTopNavigation = ({ title, classId }: Props) => {
  const t = useTranslation()
  const router = useRouter()
  const { hasProgress } = useLessonReviewProgress()

  // 퀴즈 퍼널(@use-funnel)은 문항마다 히스토리 엔트리를 쌓으므로
  // router.back() 대신 진입점인 레슨 리포트로 직접 이동한다.
  const exitToReport = () => {
    const base = buildClassroomUrl({ classId })
    router.replace(`${base}/report`)
  }

  const handleBackClick = () => {
    // 아직 아무 답변도 하지 않았으면 잃을 진행 내용이 없으므로 확인 없이 바로 나간다.
    if (!hasProgress) {
      exitToReport()
      return
    }

    openConfirmDialogV2({
      overlayId: 'lessonReviewExitConfirmDialog',
      direction: 'vertical',
      className: 'gap-8',
      ConfirmHeader: () => (
        <VStack className="gap-2 items-center">
          <Typography size="h1">{t('lessonReview.exit.title')}</Typography>
          <Typography size="h3" className="text-center text-gray-500 font-medium">
            {t('lessonReview.exit.description')}
          </Typography>
        </VStack>
      ),
      ConfirmFooter: ({ handleClose }) => (
        <HStack className="gap-2">
          <Button
            key="stay"
            variant="ghost"
            size="xLarge"
            className="w-[98px]"
            onClick={handleClose}
            data-testid="btn-lesson-review-exit-cancel"
          >
            {t('lessonReview.exit.stay')}
          </Button>
          <Button
            key="leave"
            variant="warning"
            size="xLarge"
            className="w-full"
            onClick={() => {
              handleClose()
              exitToReport()
            }}
            data-testid="btn-lesson-review-exit-confirm"
          >
            {t('lessonReview.exit.leave')}
          </Button>
        </HStack>
      ),
    })
  }

  return <TopNavigation title={title} back={handleBackClick} />
}
